import React, { useEffect, useState } from "react";
import { Plus, ShoppingCart, Trash2, Eye, Send, CheckCircle2, XCircle, PackageCheck, ArrowRightLeft, FileText, RotateCcw, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR, fmtDateShort } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const recvStatus = (total, remaining) => {
  const diff = Math.round((total - remaining) * 10000) / 10000;
  if (Math.abs(diff) < 1e-6) return { text: "Pas", cls: "text-status-passed" };
  if (diff < 0) return { text: `Kurang ${num(-diff)}`, cls: "text-amber-600" };
  return { text: `Lebih ${num(diff)}`, cls: "text-status-rejected" };
};

export default function MaterialPurchases() {
  const { can } = useAuth();
  const [tab, setTab] = useState("po");
  const write = can("production_admin", "purchasing");
  const canApprove = can("production_spv", "supervisor", "purchasing");
  return (
    <div data-testid="material-purchases-page">
      <PageHeader title="Purchase Material" subtitle="Pembelian kain & aksesori: Purchase Request → Purchase Order → Penerimaan" />
      <div className="flex gap-2 mb-4">
        <Button variant={tab === "po" ? "default" : "outline"} onClick={() => setTab("po")} data-testid="tab-po" className={tab === "po" ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"}>Purchase Order</Button>
        <Button variant={tab === "pr" ? "default" : "outline"} onClick={() => setTab("pr")} data-testid="tab-pr" className={tab === "pr" ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"}>Purchase Request</Button>
      </div>
      {tab === "pr" ? <PRTab write={write} canApprove={canApprove} /> : <POTab write={write} canApprove={canApprove} />}
    </div>
  );
}

// ---------------- PR TAB ----------------
function PRTab({ write, canApprove }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/production/material-prs").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1); const pageSize = 20;
  const filtered = list.filter((p) => !q || (`${p.number} ${p.plan_number || ""} ${p.status || ""} ${p.restock_request?.product_name || ""}`).toLowerCase().includes(q.toLowerCase()));
  useEffect(() => { setPage(1); }, [q]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const paged = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);

  const act = async (id, path, body, ok) => { try { await api.post(`/production/material-prs/${id}/${path}`, body || {}); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Hapus PR?")) return; try { await api.delete(`/production/material-prs/${id}`); toast.success("PR dihapus"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        {write ? <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-pr-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Purchase Request</Button> : <span />}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari no PR / rencana / status..." className="w-64 bg-white" data-testid="pr-search" />
      </div>
      <Panel>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>No PR</TableHead><TableHead>Rencana</TableHead><TableHead className="text-right">Item</TableHead>
            <TableHead>Sumber</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {paged.map((p) => (
              <TableRow key={p.id} data-testid={`pr-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.number}</TableCell>
                <TableCell>
                  {p.restock_request?.product_name || p.plan_number || "-"}
                  {p.restock_request && (p.restock_request.color || p.restock_request.size) && (
                    <span className="text-xs text-wmuted"> · {p.restock_request.color || "-"}/{p.restock_request.size || "-"}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {p.restock_request
                    ? <span className="font-medium text-cocoa">{Number(p.restock_request.requested_qty || 0).toLocaleString("id-ID")} pcs</span>
                    : (p.lines?.length || 0)}
                </TableCell>
                <TableCell>{p.source === "restock_prediction" ? "Stock Prediction" : p.source === "auto" ? "Otomatis" : "Manual"}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => setDetail(p)} data-testid={`pr-view-${p.id}`}><Eye className="w-4 h-4 text-cocoa" /></Button>
                  {write && p.status === "Draft" && <Button size="icon" variant="ghost" onClick={() => { setEdit(p); setOpen(true); }} data-testid={`pr-edit-${p.id}`}><Pencil className="w-4 h-4 text-cocoa" /></Button>}
                  {write && p.status === "Draft" && <Button size="icon" variant="ghost" title="Ajukan" onClick={() => act(p.id, "submit", {}, "Diajukan")} data-testid={`pr-submit-${p.id}`}><Send className="w-4 h-4 text-cocoa" /></Button>}
                  {canApprove && p.status === "Menunggu Approval" && <>
                    <Button size="icon" variant="ghost" title="Setujui" onClick={() => act(p.id, "approve", {}, "Disetujui")} data-testid={`pr-approve-${p.id}`}><CheckCircle2 className="w-4 h-4 text-status-passed" /></Button>
                    <Button size="icon" variant="ghost" title="Tolak" onClick={() => { const r = window.prompt("Alasan:"); if (r !== null) act(p.id, "reject", { reason: r }, "Ditolak"); }}><XCircle className="w-4 h-4 text-status-rejected" /></Button>
                  </>}
                  {write && p.status === "Disetujui" && <Button size="icon" variant="ghost" title="Konversi ke PO" onClick={() => setDetail({ ...p, _convert: true })} data-testid={`pr-convert-${p.id}`}><ArrowRightLeft className="w-4 h-4 text-cocoa" /></Button>}
                  {write && p.status === "Draft" && <Button size="icon" variant="ghost" title="Hapus" onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-10"><FileText className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada PR material.</TableCell></TableRow>}
          </TableBody>
        </Table>
        {filtered.length > pageSize && (
          <div className="flex items-center justify-between px-3 py-3 border-t border-taupe/15 text-xs text-wmuted" data-testid="pr-pagination">
            <span>Menampilkan {(curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, filtered.length)} dari {filtered.length}</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" className="border-taupe h-8" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="pr-prev">‹</Button>
              <span className="px-2 text-cocoa">Hal {curPage}/{totalPages}</span>
              <Button size="sm" variant="outline" className="border-taupe h-8" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} data-testid="pr-next">›</Button>
            </div>
          </div>
        )}
      </Panel>
      <PRDialog open={open} setOpen={setOpen} edit={edit} onDone={load} />
      <PRDetail pr={detail} setPr={setDetail} onDone={load} />
    </>
  );
}

function useMaterials() {
  const [fabrics, setFabrics] = useState([]);
  const [accessories, setAccessories] = useState([]);
  useEffect(() => {
    api.get("/production/fabric-options").then((r) => setFabrics(r.data));
    api.get("/production/accessories", { params: { active: "active" } }).then((r) => setAccessories(r.data));
  }, []);
  return { fabrics, accessories };
}

function MaterialLineEditor({ lines, setLines, withPrice }) {
  const { fabrics, accessories } = useMaterials();
  const setLn = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const onMat = (i, kind, id) => {
    const src = kind === "fabric" ? fabrics : accessories;
    const m = src.find((x) => x.id === id);
    setLn(i, { material_kind: kind, material_id: id, name: m?.name || "", unit: kind === "fabric" ? (m?.unit || "yard") : (m?.usage_unit || "pcs"), unit_price: withPrice ? (kind === "fabric" ? m?.price_per_unit : m?.price_per_pcs) || 0 : 0 });
  };
  return (
    <div className="space-y-2">
      {lines.map((l, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Select value={l.material_kind} onValueChange={(v) => setLn(i, { material_kind: v, material_id: "" })}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="fabric">Kain</SelectItem><SelectItem value="accessory">Aksesori</SelectItem></SelectContent>
          </Select>
          <Select value={l.material_id || "none"} onValueChange={(v) => onMat(i, l.material_kind, v === "none" ? "" : v)}>
            <SelectTrigger className="flex-1" data-testid={`ml-material-${i}`}><SelectValue placeholder="Pilih material" /></SelectTrigger>
            <SelectContent><SelectItem value="none">- Pilih -</SelectItem>{(l.material_kind === "fabric" ? fabrics : accessories).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.color ? ` (${m.color})` : ""}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLn(i, { qty: e.target.value })} className="w-24" data-testid={`ml-qty-${i}`} />
          <span className="text-xs text-wmuted w-12">{l.unit}</span>
          {withPrice && <Input type="number" placeholder="Harga" value={l.unit_price} onChange={(e) => setLn(i, { unit_price: e.target.value })} className="w-28" data-testid={`ml-price-${i}`} />}
          <Button size="icon" variant="ghost" onClick={() => setLines(lines.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="border-taupe text-cocoa" onClick={() => setLines([...lines, { material_kind: "fabric", material_id: "", name: "", unit: "yard", qty: 0, unit_price: 0 }])} data-testid="ml-add"><Plus className="w-3.5 h-3.5 mr-1" /> Tambah Material</Button>
    </div>
  );
}

function PRDialog({ open, setOpen, edit, onDone }) {
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open) return;
    setLines((edit?.lines || []).map((line) => ({ ...line })));
    setNote(edit?.note || "");
  }, [open, edit]);
  const save = async () => {
    const valid = lines.filter((l) => l.material_id && parseFloat(l.qty) > 0);
    if (!valid.length) return toast.error("Isi minimal satu material");
    const payload = {
      note,
      lines: valid.map((line) => ({
        material_kind: line.material_kind,
        material_id: line.material_id,
        name: line.name,
        unit: line.unit,
        qty: parseFloat(line.qty),
      })),
    };
    try {
      if (edit) {
        await api.put(`/production/material-prs/${edit.id}`, payload);
      } else {
        await api.post("/production/material-prs", payload);
      }
      toast.success(edit ? "PR material diperbarui" : "PR dibuat");
      setOpen(false);
      onDone();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? `Lengkapi ${edit.number}` : "Purchase Request Material"}</DialogTitle></DialogHeader>
        <MaterialLineEditor lines={lines} setLines={setLines} withPrice={false} />
        <div className="mt-2"><Label>Catatan</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} /></div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button><Button onClick={save} data-testid="save-pr-btn" className="bg-cocoa text-ivory">Simpan PR</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PRDetail({ pr, setPr, onDone }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  useEffect(() => { if (pr) { api.get("/suppliers", { params: { active: "active", partner_type: "supplier_kain,supplier_aksesori" } }).then((r) => { setSuppliers(r.data); setSupplierId(r.data[0]?.id || ""); }); } }, [pr]);
  if (!pr) return null;
  const convert = async () => { if (!supplierId) return toast.error("Pilih mitra"); try { await api.post(`/production/material-prs/${pr.id}/convert-to-po`, { supplier_id: supplierId }); toast.success("PR dikonversi ke PO (Draft)"); setPr(null); onDone(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <Dialog open={!!pr} onOpenChange={() => setPr(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{pr.number}</DialogTitle></DialogHeader>
        <Panel><Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Material</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Satuan</TableHead></TableRow></TableHeader>
          <TableBody>{pr.lines?.map((l, i) => <TableRow key={i}><TableCell>{l.name}</TableCell><TableCell className="text-right">{num(l.qty)}</TableCell><TableCell>{l.unit}</TableCell></TableRow>)}</TableBody>
        </Table></Panel>
        {pr.restock_request && (
          <div className="border border-taupe/20 p-3 space-y-2" data-testid="restock-pr-detail">
            <div className="font-semibold text-cocoa">Request dari Stock Prediction</div>
            <div className="text-sm text-wmuted">
              {pr.restock_request.product_name} · {pr.restock_request.color || "-"} /
              {" "}{pr.restock_request.size || "-"} · Request {num(pr.restock_request.requested_qty)} pcs
            </div>
            <div className={pr.restock_request.bom_found ? "text-xs text-status-passed" : "text-xs text-amber-700"}>
              {pr.restock_request.bom_found ? "BOM sudah dicek" : "BOM belum dimapping"}
            </div>
            {(pr.restock_request.materials || []).map((material) => (
              <div
                key={`${material.material_kind}-${material.material_id}`}
                className="flex justify-between text-xs border-t border-taupe/10 pt-1"
                data-testid={`restock-pr-material-${material.material_id}`}
              >
                <span>{material.name || "-"}</span>
                <span className={material.availability === "Tersedia" ? "text-status-passed" : "text-status-reject"}>
                  {num(material.available_qty)} {material.unit} · {material.availability}
                </span>
              </div>
            ))}
          </div>
        )}
        {pr._convert && (
          <div className="mt-3">
            <Label>Pilih Mitra/Supplier</Label>
            <Select value={supplierId} onValueChange={setSupplierId}><SelectTrigger data-testid="pr-convert-supplier"><SelectValue /></SelectTrigger><SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
            <Button onClick={convert} data-testid="pr-convert-btn" className="w-full mt-3 bg-cocoa text-ivory">Konversi ke Purchase Order</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- PO TAB ----------------
function POTab({ write, canApprove }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/production/material-pos").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1); const pageSize = 20;
  const filtered = list.filter((p) => !q || (`${p.number} ${p.supplier_name || ""} ${p.status || ""}`).toLowerCase().includes(q.toLowerCase()));
  useEffect(() => { setPage(1); }, [q]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const paged = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);
  const act = async (id, path, body, ok) => { try { await api.post(`/production/material-pos/${id}/${path}`, body || {}); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Hapus Purchase Order ini? Tindakan tidak bisa dibatalkan.")) return; try { await api.delete(`/production/material-pos/${id}`); toast.success("PO dihapus"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        {write ? <Button onClick={() => setOpen(true)} data-testid="add-po-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Purchase Order</Button> : <span />}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari no PO / mitra / status..." className="w-64 bg-white" data-testid="po-search" />
      </div>
      <Panel>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>No PO</TableHead><TableHead>Mitra</TableHead><TableHead className="text-right">Total</TableHead>
            <TableHead>ETA</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {paged.map((p) => (
              <TableRow key={p.id} data-testid={`po-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.number}</TableCell>
                <TableCell>{p.supplier_name}</TableCell>
                <TableCell className="text-right">{fmtIDR(p.total)}</TableCell>
                <TableCell>{fmtDateShort(p.eta)}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => setDetail(p.id)} data-testid={`po-view-${p.id}`}><Eye className="w-4 h-4 text-cocoa" /></Button>
                  {write && p.status === "Draft" && <Button size="icon" variant="ghost" title="Ajukan" onClick={() => act(p.id, "submit", {}, "Diajukan")} data-testid={`po-submit-${p.id}`}><Send className="w-4 h-4 text-cocoa" /></Button>}
                  {canApprove && p.status === "Menunggu Approval" && <Button size="icon" variant="ghost" title="Setujui" onClick={() => act(p.id, "approve", {}, "Disetujui")} data-testid={`po-approve-${p.id}`}><CheckCircle2 className="w-4 h-4 text-status-passed" /></Button>}
                  {write && p.status === "Disetujui" && <Button size="icon" variant="ghost" title="Tandai Dipesan" onClick={() => act(p.id, "order", {}, "Ditandai Dipesan")} data-testid={`po-order-${p.id}`}><ShoppingCart className="w-4 h-4 text-cocoa" /></Button>}
                  {write && ["Dipesan", "Disetujui", "Diterima Sebagian"].includes(p.status) && <Button size="icon" variant="ghost" title="Terima" onClick={() => setDetail(p.id)} data-testid={`po-receive-${p.id}`}><PackageCheck className="w-4 h-4 text-status-passed" /></Button>}
                  {write && <Button size="icon" variant="ghost" title="Hapus PO" onClick={() => del(p.id)} data-testid={`po-delete-${p.id}`}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-10"><ShoppingCart className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada PO material.</TableCell></TableRow>}
          </TableBody>
        </Table>
        {filtered.length > pageSize && (
          <div className="flex items-center justify-between px-3 py-3 border-t border-taupe/15 text-xs text-wmuted" data-testid="po-pagination">
            <span>Menampilkan {(curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, filtered.length)} dari {filtered.length}</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" className="border-taupe h-8" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="po-prev">‹</Button>
              <span className="px-2 text-cocoa">Hal {curPage}/{totalPages}</span>
              <Button size="sm" variant="outline" className="border-taupe h-8" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} data-testid="po-next">›</Button>
            </div>
          </div>
        )}
      </Panel>
      <PODialog open={open} setOpen={setOpen} onDone={load} />
      <PODetail poId={detail} setPoId={setDetail} onDone={load} write={write} />
    </>
  );
}

function PODialog({ open, setOpen, onDone }) {
  const [lines, setLines] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ supplier_id: "", eta: "", payment_term: "", discount: 0, tax: 0, shipping: 0, note: "" });
  useEffect(() => { if (open) { setLines([]); setForm({ supplier_id: "", eta: "", payment_term: "", discount: 0, tax: 0, shipping: 0, note: "" }); api.get("/suppliers", { params: { active: "active", partner_type: "supplier_kain,supplier_aksesori" } }).then((r) => setSuppliers(r.data)); } }, [open]);
  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const total = subtotal - (parseFloat(form.discount) || 0) + (parseFloat(form.tax) || 0) + (parseFloat(form.shipping) || 0);
  const save = async () => {
    if (!form.supplier_id) return toast.error("Pilih mitra");
    const valid = lines.filter((l) => l.material_id && parseFloat(l.qty) > 0);
    if (!valid.length) return toast.error("Isi minimal satu material");
    try { await api.post("/production/material-pos", { ...form, discount: parseFloat(form.discount) || 0, tax: parseFloat(form.tax) || 0, shipping: parseFloat(form.shipping) || 0, lines: valid.map((l) => ({ material_kind: l.material_kind, material_id: l.material_id, name: l.name, unit: l.unit, qty: parseFloat(l.qty), unit_price: parseFloat(l.unit_price) || 0 })) }); toast.success("PO dibuat (Draft)"); setOpen(false); onDone(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Purchase Order Material</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="col-span-2"><Label>Mitra/Supplier *</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}><SelectTrigger data-testid="po-supplier"><SelectValue placeholder="Pilih" /></SelectTrigger><SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>ETA</Label><Input type="date" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} /></div>
        </div>
        <MaterialLineEditor lines={lines} setLines={setLines} withPrice={true} />
        <div className="grid grid-cols-4 gap-3 mt-3">
          <div><Label>Termin</Label><Input value={form.payment_term} onChange={(e) => setForm({ ...form, payment_term: e.target.value })} /></div>
          <div><Label>Diskon</Label><Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></div>
          <div><Label>Pajak</Label><Input type="number" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} /></div>
          <div><Label>Ongkir</Label><Input type="number" value={form.shipping} onChange={(e) => setForm({ ...form, shipping: e.target.value })} /></div>
        </div>
        <div className="text-right mt-2"><span className="text-wmuted text-sm">Subtotal {fmtIDR(subtotal)} • </span><span className="font-heading font-bold text-cocoa text-lg">Total {fmtIDR(total)}</span></div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button><Button onClick={save} data-testid="save-po-btn" className="bg-cocoa text-ivory">Simpan PO</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PODetail({ poId, setPoId, onDone, write }) {
  const [d, setD] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [recv, setRecv] = useState({});
  const [wh, setWh] = useState("");
  const [allowOver, setAllowOver] = useState(false);
  const [overReason, setOverReason] = useState("");
  const [editReceipt, setEditReceipt] = useState(null);
  const load = () => poId && api.get(`/production/material-pos/${poId}`).then((r) => {
    setD(r.data); const init = {};
    r.data.lines.forEach((l) => {
      const rem = Math.max(0, (l.qty - (l.received_qty || 0)));
      if (l.material_kind === "fabric") init[l.line_id] = { rolls: [{ yard: l.lot || "", qty: rem }] };
      else init[l.line_id] = { qty: rem, lot: l.lot || "" };
    });
    setRecv(init);
  });
  useEffect(() => { setD(null); setAllowOver(false); setOverReason(""); load(); api.get("/warehouses", { params: { usage: "material" } }).then((r) => { setWarehouses(r.data); setWh(r.data[0]?.id || ""); }); /* eslint-disable-next-line */ }, [poId]);
  if (!poId) return null;
  const canReceive = d && ["Dipesan", "Disetujui", "Diterima Sebagian"].includes(d.status);
  const lineTotal = (l) => {
    const v = recv[l.line_id] || {};
    if (l.material_kind === "fabric") return (v.rolls || []).reduce((s, r) => s + ((parseFloat(r.yard) || 0) * (parseFloat(r.qty) || 0)), 0);
    return parseFloat(v.qty) || 0;
  };
  const setRolls = (lid, rolls) => setRecv({ ...recv, [lid]: { ...recv[lid], rolls } });
  const doReceive = async () => {
    const items = d.lines.map((l) => {
      const v = recv[l.line_id] || {};
      if (l.material_kind === "fabric") return { line_id: l.line_id, rolls: (v.rolls || []).filter((r) => parseFloat(r.qty) > 0 && parseFloat(r.yard) > 0).map((r) => ({ yard: String(r.yard || ""), qty: (parseFloat(r.yard) || 0) * (parseFloat(r.qty) || 0) })) };
      return { line_id: l.line_id, qty: parseFloat(v.qty) || 0, lot: v.lot || "" };
    }).filter((it) => (it.rolls ? it.rolls.length : it.qty > 0));
    if (!items.length) return toast.error("Isi qty/roll yang diterima");
    if (!wh) return toast.error("Pilih gudang");
    try {
      const r = await api.post(`/production/material-pos/${poId}/receive`, { warehouse_id: wh, items, allow_over: allowOver, over_reason: overReason, idempotency_key: `${poId}-${Date.now()}` });
      toast.success(`Diterima → ${r.data.status}`); load(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancelReceipt = async (r) => {
    const reason = window.prompt("Alasan pembatalan penerimaan (stok akan dikembalikan):");
    if (reason === null) return;
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    try { await api.post(`/production/material-pos/${poId}/receipts/${r.id}/cancel`, { reason }); toast.success("Penerimaan dibatalkan, stok dikembalikan"); load(); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <>
    <Dialog open={!!poId} onOpenChange={() => setPoId(null)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{d?.number} — {d?.supplier_name}</DialogTitle></DialogHeader>
        {!d ? <div className="py-10 text-center text-wmuted">Memuat...</div> : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm"><StatusBadge status={d.status} /><span className="text-wmuted">Total est.</span><span className="font-semibold text-cocoa">{fmtIDR(d.total)}</span></div>
            <div className="space-y-3">
              {d.lines.map((l) => {
                const remaining = (l.qty - (l.received_qty || 0));
                const v = recv[l.line_id] || {};
                return (
                  <div key={l.line_id} className="border border-taupe/20 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-cocoa">{l.name} <span className="text-xs text-wmuted">({l.unit})</span></div>
                      <div className="text-xs text-wmuted">Pesan {num(l.qty)} • Diterima {num(l.received_qty || 0)} • Sisa {num(remaining)}</div>
                    </div>
                    {canReceive && write && (
                      l.material_kind === "fabric" ? (
                        <div className="mt-2 space-y-1.5">
                          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-[11px] text-wmuted px-1"><span>Yard / roll</span><span>Jml roll</span><span></span></div>
                          {(v.rolls || []).map((r, ri) => (
                            <div key={ri} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                              <Input placeholder="mis. 52" value={r.yard} onChange={(e) => setRolls(l.line_id, v.rolls.map((x, i) => i === ri ? { ...x, yard: e.target.value } : x))} data-testid={`recv-roll-yard-${l.line_id}-${ri}`} />
                              <Input type="number" placeholder="jml" value={r.qty} onChange={(e) => setRolls(l.line_id, v.rolls.map((x, i) => i === ri ? { ...x, qty: e.target.value } : x))} data-testid={`recv-roll-qty-${l.line_id}-${ri}`} />
                              <Button size="icon" variant="ghost" onClick={() => setRolls(l.line_id, v.rolls.filter((_, i) => i !== ri))}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>
                            </div>
                          ))}
                          <div className="flex items-center justify-between">
                            <Button variant="outline" size="sm" className="border-taupe text-cocoa" onClick={() => setRolls(l.line_id, [...(v.rolls || []), { yard: "", qty: 0 }])} data-testid={`recv-add-roll-${l.line_id}`}><Plus className="w-3.5 h-3.5 mr-1" />Tambah Roll</Button>
                            <span className="text-sm">Total: <b className="text-cocoa">{num(lineTotal(l))}</b> yd • <b className={recvStatus(lineTotal(l), remaining).cls}>{recvStatus(lineTotal(l), remaining).text}</b></span>
                          </div>
                        </div>
                      ) : (
                        remaining > 0 || l.material_kind !== "fabric" ? (
                          <div className="mt-2 flex gap-2 items-center">
                            <Label className="text-xs">Qty</Label>
                            <Input type="number" value={v.qty ?? 0} onChange={(e) => setRecv({ ...recv, [l.line_id]: { ...v, qty: e.target.value } })} className="w-28" data-testid={`recv-qty-${l.line_id}`} />
                            <Label className="text-xs">Lot</Label>
                            <Input value={v.lot ?? ""} onChange={(e) => setRecv({ ...recv, [l.line_id]: { ...v, lot: e.target.value } })} className="w-28" />
                          </div>
                        ) : <span className="text-status-passed text-xs">Lengkap</span>
                      )
                    )}
                  </div>
                );
              })}
            </div>
            {canReceive && write && (
              <div className="border-t border-taupe/20 pt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={allowOver} onChange={(e) => setAllowOver(e.target.checked)} className="accent-cocoa" data-testid="recv-allow-over" />Izinkan terima melebihi pesanan</label>
                {allowOver && <Input placeholder="Alasan menerima lebih dari pesanan" value={overReason} onChange={(e) => setOverReason(e.target.value)} data-testid="recv-over-reason" />}
                <div className="flex items-center gap-2 justify-end">
                  <Label className="text-sm">Gudang</Label>
                  <Select value={wh} onValueChange={setWh}><SelectTrigger className="w-48" data-testid="recv-warehouse"><SelectValue /></SelectTrigger><SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select>
                  <Button onClick={doReceive} data-testid="po-do-receive-btn" className="bg-status-passed text-white"><PackageCheck className="w-4 h-4 mr-1.5" />Terima Material</Button>
                </div>
              </div>
            )}
            {d.receipts?.length > 0 && (
              <div className="border-t border-taupe/20 pt-3">
                <div className="text-sm font-semibold text-cocoa mb-2">Riwayat Penerimaan</div>
                <Table>
                  <TableHeader><TableRow className="hover:bg-transparent">
                    <TableHead>Ref</TableHead><TableHead>Item</TableHead><TableHead>Oleh</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {d.receipts.map((r) => (
                      <TableRow key={r.id} data-testid={`receipt-row-${r.id}`}>
                        <TableCell className="font-mono text-xs">{r.ref}</TableCell>
                        <TableCell className="text-xs">{(r.items || []).map((it) => `${it.name}${it.lot ? ` (${it.lot})` : ""}: ${num(it.qty)}`).join(", ")}</TableCell>
                        <TableCell className="text-xs">{r.by}</TableCell>
                        <TableCell><StatusBadge status={r.status || "Aktif"} /></TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {write && (r.status || "Aktif") === "Aktif" && <>
                            <Button size="icon" variant="ghost" title="Edit" onClick={() => setEditReceipt(r)} data-testid={`receipt-edit-${r.id}`}><Pencil className="w-4 h-4 text-cocoa" /></Button>
                            <Button size="icon" variant="ghost" title="Batalkan" onClick={() => cancelReceipt(r)} data-testid={`receipt-cancel-${r.id}`}><RotateCcw className="w-4 h-4 text-status-rejected" /></Button>
                          </>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
    {editReceipt && <ReceiptEditDialog po={d} receipt={editReceipt} onClose={() => setEditReceipt(null)} onDone={() => { setEditReceipt(null); load(); onDone(); }} />}
    </>
  );
}

function ReceiptEditDialog({ po, receipt, onClose, onDone }) {
  const lineMap = {}; po.lines.forEach((l) => { lineMap[l.line_id] = l; });
  const involved = [...new Set((receipt.items || []).map((it) => it.line_id))].map((lid) => lineMap[lid]).filter(Boolean);
  const buildInit = () => {
    const init = {};
    involved.forEach((l) => { init[l.line_id] = l.material_kind === "fabric" ? { rolls: [] } : { qty: 0, lot: "" }; });
    (receipt.items || []).forEach((it) => {
      const l = lineMap[it.line_id]; if (!l) return;
      if (l.material_kind === "fabric") { const y = Number(it.lot) || 0; init[it.line_id].rolls.push({ yard: it.lot || "", qty: y > 0 ? Math.round((Number(it.qty) / y) * 100) / 100 : it.qty }); }
      else { init[it.line_id].qty = it.qty; init[it.line_id].lot = it.lot || ""; }
    });
    return init;
  };
  const [recv, setRecv] = useState(buildInit());
  const [allowOver, setAllowOver] = useState(false);
  const [overReason, setOverReason] = useState("");
  const setRolls = (lid, rolls) => setRecv({ ...recv, [lid]: { ...recv[lid], rolls } });
  const save = async () => {
    const items = involved.map((l) => {
      const v = recv[l.line_id] || {};
      if (l.material_kind === "fabric") return { line_id: l.line_id, rolls: (v.rolls || []).filter((r) => parseFloat(r.qty) > 0 && parseFloat(r.yard) > 0).map((r) => ({ yard: String(r.yard || ""), qty: (parseFloat(r.yard) || 0) * (parseFloat(r.qty) || 0) })) };
      return { line_id: l.line_id, qty: parseFloat(v.qty) || 0, lot: v.lot || "" };
    }).filter((it) => (it.rolls ? it.rolls.length : it.qty > 0));
    if (!items.length) return toast.error("Isi item yang benar");
    try {
      const r = await api.put(`/production/material-pos/${po.id}/receipts/${receipt.id}`, { warehouse_id: receipt.warehouse_id, items, allow_over: allowOver, over_reason: overReason, idempotency_key: `${po.id}-edit-${Date.now()}` });
      toast.success(`Penerimaan dikoreksi → ${r.data.status}`); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Edit Penerimaan {receipt.ref}</DialogTitle></DialogHeader>
        <p className="text-xs text-wmuted -mt-2">Ubah angka bila salah input. Sistem membalik penerimaan lama lalu menerapkan yang baru (hanya bila material belum dipakai).</p>
        <div className="space-y-3">
          {involved.map((l) => {
            const v = recv[l.line_id] || {};
            const oldForLine = (receipt.items || []).filter((it) => it.line_id === l.line_id).reduce((s, it) => s + (Number(it.qty) || 0), 0);
            const already = (Number(l.received_qty) || 0) - oldForLine;
            const remaining = Math.round(((Number(l.qty) || 0) - already) * 10000) / 10000;
            const total = l.material_kind === "fabric" ? (v.rolls || []).reduce((s, r) => s + ((parseFloat(r.yard) || 0) * (parseFloat(r.qty) || 0)), 0) : (parseFloat(v.qty) || 0);
            const st = recvStatus(total, remaining);
            return (
              <div key={l.line_id} className="border border-taupe/20 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-cocoa">{l.name} <span className="text-xs text-wmuted">({l.unit})</span></div>
                  <div className="text-xs text-wmuted">Pesan {num(l.qty)} • Sisa {num(remaining)}</div>
                </div>
                {l.material_kind === "fabric" ? (
                  <div className="mt-2 space-y-1.5">
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-[11px] text-wmuted px-1"><span>Yard / roll</span><span>Jml roll</span><span></span></div>
                    {(v.rolls || []).map((r, ri) => (
                      <div key={ri} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                        <Input placeholder="mis. 52" value={r.yard} onChange={(e) => setRolls(l.line_id, v.rolls.map((x, i) => i === ri ? { ...x, yard: e.target.value } : x))} data-testid={`edit-roll-yard-${l.line_id}-${ri}`} />
                        <Input type="number" placeholder="jml" value={r.qty} onChange={(e) => setRolls(l.line_id, v.rolls.map((x, i) => i === ri ? { ...x, qty: e.target.value } : x))} data-testid={`edit-roll-qty-${l.line_id}-${ri}`} />
                        <Button size="icon" variant="ghost" onClick={() => setRolls(l.line_id, v.rolls.filter((_, i) => i !== ri))}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="border-taupe text-cocoa" onClick={() => setRolls(l.line_id, [...(v.rolls || []), { yard: "", qty: 0 }])} data-testid={`edit-add-roll-${l.line_id}`}><Plus className="w-3.5 h-3.5 mr-1" />Tambah Roll</Button>
                  </div>
                ) : (
                  <div className="mt-2 flex gap-2 items-center">
                    <Label className="text-xs">Qty</Label>
                    <Input type="number" value={v.qty ?? 0} onChange={(e) => setRecv({ ...recv, [l.line_id]: { ...v, qty: e.target.value } })} className="w-28" data-testid={`edit-qty-${l.line_id}`} />
                    <Label className="text-xs">Lot</Label>
                    <Input value={v.lot ?? ""} onChange={(e) => setRecv({ ...recv, [l.line_id]: { ...v, lot: e.target.value } })} className="w-28" />
                  </div>
                )}
                <div className="text-right text-sm mt-2" data-testid={`edit-total-${l.line_id}`}>Total diisi: <b className="text-cocoa">{num(total)}</b> {l.unit} • <b className={st.cls}>{st.text}</b></div>
              </div>
            );
          })}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={allowOver} onChange={(e) => setAllowOver(e.target.checked)} className="accent-cocoa" data-testid="edit-allow-over" />Izinkan terima melebihi pesanan</label>
          {allowOver && <Input placeholder="Alasan menerima lebih" value={overReason} onChange={(e) => setOverReason(e.target.value)} data-testid="edit-over-reason" />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="edit-receipt-save" className="bg-cocoa text-ivory">Simpan Koreksi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
